/*
 * Lazy-load the heavy navigation markup (2026-08-09 rebase, v2;
 * 2026-09-03 image gating, v3).
 *
 * Two payloads are no longer baked into every page's initial HTML:
 *   - the desktop "Shop" mega flyout panel (about 1.0 MB, 2,000+ links)
 *   - the mobile drawer menu (12 card images; these were the images Google
 *     lifted as brand-page thumbnails)
 * Each ships a small placeholder and the real content is fetched once, on
 * idle or on first menu open, whichever comes first.
 *
 * Source of truth: /cart?view=nav-panel (an alternate template with
 * `layout none`, so the response is ONLY the panel, no theme chrome).
 *
 * v3 (2026-09-03, Google thumbnail fix): Googlebot renders pages with
 * JavaScript and waits for idle, so it sees the injected panel. That is
 * wanted for the panel's LINKS (internal linking stays exactly as before),
 * but its 60+ brand logos and category tiles were being attached to every
 * product page in Google Images and shown as wrong thumbnails in search
 * results. The panel images are therefore rendered with data-src only (no
 * src) in snippets/mega-nav-panel.liquid, and promoted to src here on the
 * first human input (pointer, touch, key, wheel, scroll) or on menu open.
 * Googlebot produces neither, so it never sees an image URL to index.
 *
 * Load-bearing details, each caught by live testing:
 *   1. The extract selector MUST be scoped to #nav-panel-source. The
 *      placeholders in the header carry the same data-mega-panel-key, come
 *      FIRST in the document, and are tiny; an unscoped query matches them
 *      first and would inject a placeholder into itself, leaving the menu
 *      permanently empty.
 *   2. The toggle listener MUST use the capture phase. The native <details>
 *      "toggle" event does not bubble, so a normal delegated listener on
 *      document never fires.
 *   3. Injection SWAPS THE PLACEHOLDER OUT (insert siblings, remove the
 *      placeholder) instead of filling it, so the final DOM structure is
 *      byte-equivalent to the old inline markup. No wrapper element is left
 *      to disturb CSS combinators or flyout geometry.
 *   4. After each swap a bubbling "nav-panel:injected" event fires from the
 *      parent element. custom.js listens for it and binds its menu handlers
 *      (accordions, tabs, sidebar hover) to the freshly injected subtree;
 *      those bindings are load-time-only otherwise and would be dead here.
 *   5. Image promotion is scoped to the desktop panel (.js-mega-nav) and the
 *      mobile drawer (.main-menu__disclosure). Product zoom images (also
 *      data-src) are never touched. The mobile drawer cards had no promoter
 *      at all after the v2 injection (blank circles on phones since
 *      2026-08-12); the toggle path now promotes them on open.
 */
(function () {
  var ENDPOINT = '/cart?view=nav-panel';
  var fetchPromise = null;
  var humanSeen = false;

  // Desktop panel images (data-src since v3) and the mobile drawer cards
  // (data-src since v2; nothing promoted them after injection, so the drawer
  // showed blank circles from 2026-08-12 until this fix).
  var MENU_IMG_SELECTOR =
    '.js-mega-nav img[data-src]:not([src]), .main-menu__disclosure img[data-src]:not([src])';

  function promoteMenuImages() {
    document.querySelectorAll(MENU_IMG_SELECTOR).forEach(function (img) {
      img.setAttribute('src', img.getAttribute('data-src'));
    });
  }

  function getPanelsMap() {
    if (!fetchPromise) {
      fetchPromise = fetch(ENDPOINT, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('nav panel fetch failed: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var map = {};
          // scoped: never match the placeholders (see note 1 above)
          doc.querySelectorAll('#nav-panel-source [data-mega-panel-key]')
            .forEach(function (el) {
              map[el.getAttribute('data-mega-panel-key')] = el.innerHTML;
            });
          return map;
        })
        .catch(function (e) {
          // Allow a later attempt (menu open) to retry a transient failure.
          fetchPromise = null;
          if (window.console && console.warn) console.warn(e);
          return {};
        });
    }
    return fetchPromise;
  }

  function fillAllPending() {
    var pending = document.querySelectorAll('[data-mega-panel-pending]');
    if (!pending.length) {
      if (humanSeen) promoteMenuImages();
      return;
    }
    getPanelsMap().then(function (map) {
      document.querySelectorAll('[data-mega-panel-pending]').forEach(function (el) {
        var key = el.getAttribute('data-mega-panel-key');
        var html = map[key];
        // Only swap in real content; never remove a placeholder for nothing.
        if (html && html.length) {
          var parent = el.parentNode;
          el.insertAdjacentHTML('beforebegin', html);
          parent.removeChild(el);
          try {
            parent.dispatchEvent(
              new CustomEvent('nav-panel:injected', { bubbles: true })
            );
          } catch (err) {
            if (window.console && console.warn) console.warn(err);
          }
        }
      });
      // A human already touched the page: give the fresh panel its images.
      if (humanSeen) promoteMenuImages();
    });
  }

  function schedulePrefetch() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fillAllPending, { timeout: 3000 });
    } else {
      setTimeout(fillAllPending, 1500);
    }
  }

  if (document.readyState === 'complete') {
    schedulePrefetch();
  } else {
    window.addEventListener('load', schedulePrefetch);
  }

  // First human input promotes the menu images (see v3 note). Passive and
  // self-removing, so no cost after the first event. v3.1: 'scroll' is
  // deliberately NOT in the list (a carousel or scrollIntoView call fires it
  // with no human present) and untrusted (script-dispatched) events are
  // ignored, so only a real pointer, touch, key or wheel can promote.
  var HUMAN_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];

  function onFirstHuman(e) {
    if (humanSeen) return;
    if (e && e.isTrusted === false) return;
    humanSeen = true;
    HUMAN_EVENTS.forEach(function (name) {
      window.removeEventListener(name, onFirstHuman, true);
    });
    promoteMenuImages();
  }

  HUMAN_EVENTS.forEach(function (name) {
    window.addEventListener(name, onFirstHuman, { capture: true, passive: true });
  });

  // Capture phase: see note 2 above. Covers both the desktop mega-nav
  // (.js-mega-nav) and the mobile drawer (.main-menu__disclosure), so a fast
  // hamburger tap fetches immediately instead of waiting for idle.
  document.addEventListener(
    'toggle',
    function (e) {
      var d = e.target;
      if (
        d &&
        d.open &&
        d.matches &&
        d.matches('.js-mega-nav, .main-menu__disclosure')
      ) {
        humanSeen = true;
        fillAllPending();
      }
    },
    true
  );
})();
